import type { Addon } from '@/shared/lib/db/schemas/cart.schema';

export type AddonsLimitValidation = {
  isValid: boolean;
  message: string;
  /** Categoria que excedeu o limite, quando houver. */
  categoryId?: string;
};

/**
 * Valida o "máximo por complemento escolhido" (issue 311) sobre os adicionais
 * de um item do carrinho, considerando DOIS tetos independentes:
 *
 * 1. Teto individual do complemento (`item_select_max`): a quantidade de UM
 *    mesmo complemento não pode ultrapassar seu próprio teto. `item_select_max`
 *    ausente ou `<= 0` indica complemento sem teto individual e é ignorado.
 * 2. Teto da categoria (`select_max`): para cada categoria, a SOMA das
 *    quantidades dos adicionais escolhidos não pode ultrapassar `select_max`.
 *    Um `select_max` ausente ou `<= 0` indica categoria sem teto e é ignorado.
 *
 * O teto individual é verificado primeiro para que a mensagem impeditiva aponte
 * o complemento específico. Espelha o bloqueio do seletor da tela do produto,
 * porém opera sobre o `Addon` do carrinho (que carrega ambos os tetos por
 * adicional) para revalidar o limite nos casos de uso do carrinho e no fluxo
 * make-order — sem depender de a tela do produto ter sido a origem da seleção
 * (defesa em profundidade / default seguro).
 */
export function validateAddonsLimit(addons: Addon[] | undefined): AddonsLimitValidation {
  if (!Array.isArray(addons) || addons.length === 0) {
    return { isValid: true, message: '' };
  }

  // Teto individual do complemento: cada complemento, isoladamente, não pode
  // ultrapassar seu próprio teto — mesmo que a categoria ainda tivesse espaço.
  for (const addon of addons) {
    const itemSelectMax = Number(addon.item_select_max) || 0;
    if (itemSelectMax <= 0) continue; // complemento sem teto individual

    const quantity = Number(addon.quantity) || 0;
    if (quantity > itemSelectMax) {
      return {
        isValid: false,
        message: `Selecione no máximo ${itemSelectMax} ${
          itemSelectMax === 1 ? 'unidade' : 'unidades'
        } de ${addon.name}.`,
        categoryId: addon.addon_category_id,
      };
    }
  }

  // Soma das quantidades e maior teto informado, por categoria. Diferentes
  // adicionais da mesma categoria devem trazer o mesmo `select_max`; usamos o
  // maior valor observado como salvaguarda contra dados inconsistentes.
  const totals = new Map<string, { total: number; selectMax: number }>();

  for (const addon of addons) {
    const categoryId = addon.addon_category_id;
    if (!categoryId) continue;

    const quantity = Number(addon.quantity) || 0;
    const selectMax = Number(addon.select_max) || 0;

    const current = totals.get(categoryId) ?? { total: 0, selectMax: 0 };
    current.total += quantity;
    current.selectMax = Math.max(current.selectMax, selectMax);
    totals.set(categoryId, current);
  }

  for (const [categoryId, { total, selectMax }] of totals) {
    if (selectMax <= 0) continue; // categoria sem teto

    if (total > selectMax) {
      return {
        isValid: false,
        message: `Selecione no máximo ${selectMax} ${
          selectMax === 1 ? 'complemento' : 'complementos'
        } por categoria.`,
        categoryId,
      };
    }
  }

  return { isValid: true, message: '' };
}
